import { notificationsSlice } from 'features/notifications/notificationsSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimActKc2OtherCreateInput, BimActKc2OtherUpdateInput, BimCreateActKc2OtherMutation } from 'gql/graphql';
import { CostGroupEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import type { TActionCommonInterface } from 'utils/types';

import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { bimCreateActKC2OtherGql, bimUpdateActKC2OtherGql } from '../../bimWorkOrderQueries';

const requestId = 'bimUpdateActKC2Other';

export const useBimCreateOrUpdateActKC2Other = (
    actKC2OtherId?: number,
    onSuccess?: () => void,
    priceTableApiRef?: React.RefObject<any>,
) => {
    const dispatch = useDispatch();
    const { id: projectId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [bimUpdateActKC2OtherReset] = useResetStoreData({ requestId });

    const onSuccessUpdateLocal = useCallback(() => {
        bimUpdateActKC2OtherReset(undefined);
        onSuccess?.();
    }, [bimUpdateActKC2OtherReset, onSuccess]);

    const onSuccessCreateLocal = useCallback(
        (data: TActionCommonInterface<BimCreateActKc2OtherMutation>) => {
            bimUpdateActKC2OtherReset(undefined);
            onSuccess?.();

            const { bimCreateActKC2Other } = data?.payload ?? {};

            sendGpnAnalyticsEvent({
                componentId: 'rim_act KC file_saved',
                projectId,
                orderId: bimCreateActKC2Other?.data?.workOrderId,
                actKC2Id: bimCreateActKC2Other?.data?.id,
                actKC2Status: bimCreateActKC2Other?.data?.status,
                methodology: projectData?.methodology,
                projectName: projectData?.name,
                projectType: projectData?.contractType,
                projectDO: projectData?.subsidiary?.name,
                costGroup: CostGroupEnum.Others,
            });

            dispatch(
                notificationsSlice.actions.add({
                    key: 'createOtherActKC2Notification',
                    message: 'Акт КС-2 успешно добавлен',
                    status: 'success',
                    autoClose: 15,
                }),
            );

            setTimeout(() => {
                priceTableApiRef?.current?.current?.api?.ensureColumnVisible('completedValueOthers', 'start');
            }, 200);
        },
        [
            bimUpdateActKC2OtherReset,
            dispatch,
            onSuccess,
            priceTableApiRef,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            sendGpnAnalyticsEvent,
        ],
    );

    const [, bimCreateActKC2OtherStatus, , bimCreateActKC2OtherRequest] = useRequest({
        requestId,
        query: bimCreateActKC2OtherGql,
        withEffect: false,
        dependenciesUpdater: onSuccessCreateLocal,
    });

    const [, bimUpdateActKC2OtherStatus, , bimUpdateActKC2OtherRequest] = useRequest({
        requestId,
        query: bimUpdateActKC2OtherGql,
        withEffect: false,
        dependenciesUpdater: onSuccessUpdateLocal,
    });

    const bimCreateActKC2OtherMakeRequest = useCallback(
        (input: BimActKc2OtherCreateInput) => {
            dispatch(bimCreateActKC2OtherRequest({ input }));
        },
        [bimCreateActKC2OtherRequest, dispatch],
    );

    const bimUpdateActKC2OtherMakeRequest = useCallback(
        (input: BimActKc2OtherUpdateInput) => {
            dispatch(bimUpdateActKC2OtherRequest({ input }));
        },
        [bimUpdateActKC2OtherRequest, dispatch],
    );

    const status = useMemo(
        () => (actKC2OtherId ? bimUpdateActKC2OtherStatus : bimCreateActKC2OtherStatus),
        [actKC2OtherId, bimCreateActKC2OtherStatus, bimUpdateActKC2OtherStatus],
    );

    return {
        bimCreateActKC2OtherMakeRequest,
        bimUpdateActKC2OtherMakeRequest,
        status,
    };
};
